/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  SSO_SIGNUP_PROVIDER_ORDER_DEFAULT,
  type SsoSignupFeatureFlags,
  type SsoSignupProviderId,
} from '../constants/featureFlags';
import type { SocialProvider } from '../managers';

const KNOWN_PROVIDERS: ReadonlySet<string> = new Set(
  SSO_SIGNUP_PROVIDER_ORDER_DEFAULT,
);

// Unknown ids dropped; missing providers appended (flags control visibility).
export const resolveSocialProviderOrder = (
  value: unknown,
): SsoSignupProviderId[] => {
  const parsed: SsoSignupProviderId[] = [];
  const seen = new Set<SsoSignupProviderId>();

  if (Array.isArray(value)) {
    for (const item of value) {
      if (typeof item !== 'string') continue;
      const provider = item.toLowerCase() as SsoSignupProviderId;
      if (!KNOWN_PROVIDERS.has(provider) || seen.has(provider)) continue;
      seen.add(provider);
      parsed.push(provider);
    }
  }

  const rest = SSO_SIGNUP_PROVIDER_ORDER_DEFAULT.filter(
    (provider) => !seen.has(provider),
  );
  return parsed.length > 0
    ? [...parsed, ...rest]
    : [...SSO_SIGNUP_PROVIDER_ORDER_DEFAULT];
};

export type SocialProviderVisibility = Partial<
  Record<SocialProvider, boolean | undefined>
>;

export type SocialProviderFlagSource = Pick<
  SsoSignupFeatureFlags,
  'facebook_login' | 'google_login' | 'apple_login' | 'wechat_login'
>;

export const toSocialProviderVisibility = (
  flags: SocialProviderFlagSource,
): SocialProviderVisibility => ({
  facebook: flags.facebook_login,
  google: flags.google_login,
  apple: flags.apple_login,
  wechat: flags.wechat_login,
});

// Drops providers explicitly hidden by feature flags.
export const applySocialProviderVisibility = <T>(
  providers: Partial<Record<SocialProvider, T>>,
  visibility: SocialProviderVisibility,
): Partial<Record<SocialProvider, T>> => {
  const next: Partial<Record<SocialProvider, T>> = { ...providers };

  (Object.keys(visibility) as SocialProvider[]).forEach((provider) => {
    if (visibility[provider] === false) {
      next[provider] = undefined;
    }
  });

  return next;
};

export const hasVisibleSocialProviders = <T>(
  providers: Partial<Record<SocialProvider, T | undefined>>,
): boolean => Object.values(providers).some((provider) => provider != null);
